/**
 * @param {string|URL|Request|Response|{ url?: string }|null|undefined} requestOrResponse
 * @returns {string} Request URL (may be relative) or response URL, or "".
 */
export function getRequestUrl(requestOrResponse) {
  if (requestOrResponse == null) {
    return "";
  }
  if (typeof requestOrResponse === "string") {
    return requestOrResponse;
  }
  if (requestOrResponse instanceof URL) {
    return requestOrResponse.href;
  }
  if (typeof Request !== "undefined" && requestOrResponse instanceof Request) {
    return requestOrResponse.url;
  }
  if (typeof Response !== "undefined" && requestOrResponse instanceof Response) {
    return requestOrResponse.url || "";
  }
  if (typeof requestOrResponse === "object" && "url" in requestOrResponse) {
    const u = requestOrResponse.url;
    return typeof u === "string" ? u : "";
  }
  return "";
}

/**
 * Whether `url` targets the same origin as the configured API (`apiBase`).
 *
 * **Contract:** Pass `apiBase` exactly as in `src/config.js` (`API_URL`): a normalized
 * origin with no trailing slash. Callers should not rely on slash-stripping; the
 * `(apiBase || "").replace(/\/$/, "")` line is defensive backward compatibility only.
 *
 * @param {string} url Request URL (absolute or relative to `locationHref`).
 * @param {string} apiBase Normalized API origin from config (no trailing slash).
 * @param {string} [locationHref] Current document URL; defaults to `window.location.href` in the browser.
 * @returns {boolean}
 */
export function isApiRequest(url, apiBase, locationHref) {
  const base = (apiBase || "").replace(/\/$/, "");
  if (base === "" || !url) {
    return false;
  }
  const pageHref =
    locationHref != null && locationHref !== ""
      ? locationHref
      : typeof window !== "undefined" && window?.location?.href
        ? window.location.href
        : "http://localhost/";
  let absolute;
  let absoluteBase;
  try {
    absolute = new URL(url, pageHref).href;
    absoluteBase = new URL(base, pageHref).href.replace(/\/$/, "");
  } catch {
    return false;
  }
  return (
    absolute === absoluteBase ||
    absolute.startsWith(`${absoluteBase}/`) ||
    absolute.startsWith(`${absoluteBase}?`) ||
    absolute.startsWith(`${absoluteBase}#`)
  );
}
